import React from "react";
import { Paper, Group, Text, ActionIcon, Box, Stack } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BACKGROUND_WHITE = "white";
const COLOR_TEXT_GRAY = "#6c757d";
const COLOR_BLUE_100 = "#1976d2";
const COLOR_BLUE_200 = "#42a5f5";
const COLOR_BLUE_300 = "#90caf9";
const COLOR_BLUE_400 = "#bbdefb";
const COLOR_BLUE_500 = "#e3f2fd";

type IconStyle = "box" | "actionIcon";

interface WebsiteGeneratorOptionCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  subtitle?: string; // Alias for description
  onClick: () => void;
  iconStyle?: IconStyle; // "box" (default) or "actionIcon"
}

const WebsiteGeneratorOptionCard: React.FC<WebsiteGeneratorOptionCardProps> = ({
  icon,
  title,
  description,
  subtitle,
  onClick,
  iconStyle = "box",
}) => {
  // Use description if provided, otherwise use subtitle, empty string if neither
  const descriptionText = description ?? subtitle ?? "";

  return (
    <Paper
      p="md"
      style={{
        border: `1px solid ${COLOR_BORDER_LIGHT}`,
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = COLOR_BACKGROUND_LIGHT;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = COLOR_BACKGROUND_WHITE;
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap="sm">
          {/* Icon Container - Conditional rendering based on iconStyle */}
          {iconStyle === "box" ? (
            <Box
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: COLOR_BACKGROUND_LIGHT,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          ) : (
            <ActionIcon variant="light" color="gray" size="lg" radius="md">
              {icon}
            </ActionIcon>
          )}
          <Stack gap={0}>
            <Text fw={500} size="sm">
              {title}
            </Text>
            {descriptionText && (
              <Text size="xs" c="dimmed">
                {descriptionText}
              </Text>
            )}
          </Stack>
        </Group>
        <ActionIcon variant="subtle" color="gray">
          <IconChevronRight size={16} />
        </ActionIcon>
      </Group>
    </Paper>
  );
};

// Helper components for specific icons
export const ThemeIcon = () => (
  <Text
    style={{
      fontSize: "18px",
      fontWeight: "bold",
      color: COLOR_TEXT_GRAY,
      fontFamily: "monospace",
      letterSpacing: "-1px",
    }}
  >
    Aa
  </Text>
);

export const FontsIcon = () => (
  <Stack
    gap={0}
    style={{
      fontSize: "12px",
      fontWeight: "bold",
      color: COLOR_TEXT_GRAY,
      fontFamily: "monospace",
      lineHeight: 1.2,
    }}
  >
    <Text style={{ fontSize: "14px", fontWeight: "bold" }}>H</Text>
    <Text style={{ fontSize: "8px", fontWeight: "normal" }}>body</Text>
  </Stack>
);

export const ColorsIcon = () => (
  <Group gap="2px">
    <Box
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: COLOR_BLUE_100,
      }}
    />
    <Box
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: COLOR_BLUE_200,
      }}
    />
    <Box
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: COLOR_BLUE_300,
      }}
    />
    <Box
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: COLOR_BLUE_400,
      }}
    />
    <Box
      style={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: COLOR_BLUE_500,
      }}
    />
  </Group>
);

export default WebsiteGeneratorOptionCard;
